import type { TokenPair } from "@shared/api/generated/model";
import axios, { type AxiosInstance } from "axios";

const API_BASE = "/api";

const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15_000,
});

export const orvalClient = axios.create({
  baseURL: "",
  withCredentials: true,
  timeout: 45_000,
});

const authClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15_000,
});

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Синхронизация токенов между вкладками
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "acweb_refresh" && event.newValue === null) {
      // Другая вкладка сделала logout — редирект на логин
      window.location.href = "/auth/login";
    }
  });
}

const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;

  // Проверяем актуальный токен из localStorage (мог обновиться в другой вкладке)
  const refresh = typeof window !== "undefined" ? localStorage.getItem("acweb_refresh") : null;
  if (!refresh) {
    isRefreshing = false;
    return null;
  }

  refreshPromise = authClient
    .post<{ tokens: TokenPair }>("/auth/refresh", { refresh })
    .then((res) => {
      const token = res.data?.tokens?.access_token;
      const newRefresh = res.data?.tokens?.refresh_token;
      if (typeof window !== "undefined") {
        if (token) {
          localStorage.setItem("acweb_access", token);
        }
        if (newRefresh) {
          localStorage.setItem("acweb_refresh", newRefresh);
        } else {
          // Критично: если сервер не вернул новый refresh — удаляем старый
          console.error("[Auth] Refresh response missing refresh_token");
        }
      }
      return token ?? null;
    })
    .catch((err) => {
      // Логируем ошибку для диагностики
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.message || "Unknown error";
      console.error(`[Auth] Refresh failed: ${status} - ${message}`);

      // Для 401/403 — токен невалиден, не пытаемся повторно
      if (status === 401 || status === 403) {
        return null;
      }

      // Для сетевых ошибок — возвращаем null (интерцептор сделает редирект)
      return null;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

const attachInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use((config) => {
    if (typeof window === "undefined") {
      return config;
    }
    const token = localStorage.getItem("acweb_access");
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const path = error.config?.url ?? "";
      const isLoginPath =
        typeof path === "string" &&
        (path.startsWith("/auth/") || path.startsWith("/api/auth/")) &&
        !path.includes("/auth/me/");
      const _isOtpPath =
        typeof path === "string" &&
        (path.startsWith("/auth/otp/") || path.startsWith("/api/auth/otp/"));

      if (error.response?.status === 401 && !isLoginPath) {
        return refreshAccessToken().then((newToken) => {
          if (newToken) {
            const config = error.config;
            if (config) {
              config.headers = config.headers ?? {};
              config.headers.Authorization = `Bearer ${newToken}`;
              return instance.request(config);
            }
          }
          if (typeof window !== "undefined") {
            localStorage.removeItem("acweb_access");
            localStorage.removeItem("acweb_refresh");
            window.location.href = "/auth/login";
          }
          return Promise.reject(error);
        });
      }
      // 403 означает отсутствие прав доступа, а не невалидный токен
      // Не редиректим на /auth/login, просто прокидываем ошибку
      return Promise.reject(error);
    },
  );
};

attachInterceptors(client);
attachInterceptors(orvalClient);

export default client;
